import { MapPin, Save, Loader2, Home, Building, User, Navigation } from 'lucide-react';
import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAddress } from '@/context/AddressContext';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const AddEditAddressPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { createAddress, updateAddress } = useAddress();
  const { user } = useAuth();
  const { toast } = useToast();

  const address = location.state?.address;
  const isEditing = !!address;

  const [formData, setFormData] = useState({
    fullName: address?.fullName || '',
    phoneNumber: address?.phoneNumber || '',
    addressLine1: address?.addressLine1 || '',
    addressLine2: address?.addressLine2 || '',
    city: address?.city || '',
    state: address?.state || '',
    postalCode: address?.postalCode || '',
    country: address?.country || 'India',
    addressType: address?.addressType || 'HOME',
    isDefault: address?.isDefault || false,
    deliveryInstructions: address?.deliveryInstructions || '',
  });

  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const phoneRef = useRef<HTMLInputElement>(null);

  // Show authentication message if user is not authenticated
  if (!user?.isAuthenticated || !user?.customerId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
            <MapPin className="h-10 w-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Sign in to manage addresses</h1>
          <p className="text-muted-foreground mb-8">Please sign in with your account to manage your delivery addresses.</p>
          <Button variant="cta" onClick={() => navigate('/auth/signin')}>
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  // Detect current location
  const detectMyLocation = async () => {
    if (locationLoading) return;
    
    if (!navigator.geolocation) {
      toast({
        title: 'Location Not Supported',
        description: 'Your browser does not support location detection.',
        variant: 'destructive',
      });
      return;
    }

    setLocationLoading(true);
    setErrors({});

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=jsonv2&addressdetails=1&zoom=18&accept-language=en`;

          const response = await fetch(url, {
            headers: {
              'User-Agent': 'AuraKart/1.0',
            },
          });

          if (!response.ok) throw new Error('Geocoding failed');

          const data = await response.json();
          const a = data.address || {};

          const addressLine1 = [
            a.house_number,
            a.road,
            a.neighbourhood,
            a.suburb,
          ]
            .filter(Boolean)
            .join(', ');

          setFormData((prev) => ({
            ...prev,
            addressLine1,
            city: a.city || a.town || a.village || a.state_district || '',
            state: a.state || '',
            postalCode: a.postcode || '',
            country: a.country || 'India',
          }));

          toast({
            title: 'Location Detected',
            description: 'Please review and complete the address details.',
          });
        } catch (error) {
          toast({
            title: 'Detection Failed',
            description: 'Unable to detect address. Please enter manually.',
            variant: 'destructive',
          });
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        toast({
          title: 'Location Error',
          description: 'Unable to fetch location. Please check permissions.',
          variant: 'destructive',
        });
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 10000,
      }
    );
  };

  // Validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, '')))
      newErrors.phoneNumber = 'Must be 10 digits';
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!/^\d{6}$/.test(formData.postalCode.replace(/\D/g, '')))
      newErrors.postalCode = 'Must be 6 digits';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save address
  const handleSave = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the highlighted fields.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        phoneNumber: formData.phoneNumber.replace(/\D/g, ''),
        postalCode: formData.postalCode.replace(/\D/g, ''),
      };

      if (isEditing) {
        await updateAddress(address.addressId, payload);
      } else {
        await createAddress(payload);
      }

      toast({
        title: 'Success',
        description: 'Address saved successfully',
      });
      
      navigate('/addresses');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save address',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const addressTypes = [
    { label: 'Home', value: 'HOME', icon: Home },
    { label: 'Work', value: 'WORK', icon: Building },
    { label: 'Other', value: 'OTHER', icon: User },
  ];

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold">
            {isEditing ? 'Edit Address' : 'Add New Address'}
          </h1>
          <Button
            variant="ghost"
            onClick={() => navigate('/addresses')}
            className="text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Location Detection */}
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={detectMyLocation}
              disabled={locationLoading}
              className="w-full md:w-auto border-primary text-primary hover:bg-primary hover:text-white"
            >
              {locationLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Navigation className="h-4 w-4 mr-2" />
              )}
              {locationLoading ? 'Detecting...' : 'Use My Current Location'}
            </Button>
          </div>

          <div className="space-y-6">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => updateFormData('fullName', e.target.value)}
                className={errors.fullName ? 'border-destructive' : ''}
                onKeyDown={(e) => e.key === 'Enter' && phoneRef.current?.focus()}
              />
              {errors.fullName && (
                <p className="text-sm text-destructive">{errors.fullName}</p>
              )}
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number *</Label>
              <Input
                ref={phoneRef}
                id="phoneNumber"
                type="tel"
                maxLength={10}
                value={formData.phoneNumber}
                onChange={(e) => updateFormData('phoneNumber', e.target.value)}
                className={errors.phoneNumber ? 'border-destructive' : ''}
                placeholder="10-digit mobile number"
              />
              {errors.phoneNumber && (
                <p className="text-sm text-destructive">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Address Line 1 */}
            <div className="space-y-2">
              <Label htmlFor="addressLine1">Address Line 1 *</Label>
              <Input
                id="addressLine1"
                value={formData.addressLine1}
                onChange={(e) => updateFormData('addressLine1', e.target.value)}
                className={errors.addressLine1 ? 'border-destructive' : ''}
                placeholder="House/Flat No., Building Name"
              />
              {errors.addressLine1 && (
                <p className="text-sm text-destructive">{errors.addressLine1}</p>
              )}
            </div>

            {/* Address Line 2 */}
            <div className="space-y-2">
              <Label htmlFor="addressLine2">Address Line 2</Label>
              <Input
                id="addressLine2"
                value={formData.addressLine2}
                onChange={(e) => updateFormData('addressLine2', e.target.value)}
                placeholder="Street, Locality"
              />
            </div>

            {/* City and State */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  className={errors.city ? 'border-destructive' : ''}
                />
                {errors.city && (
                  <p className="text-sm text-destructive">{errors.city}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <select
                  id="state"
                  value={formData.state}
                  onChange={(e) => updateFormData('state', e.target.value)}
                  className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                    errors.state ? 'border-destructive' : ''
                  }`}
                >
                  <option value="">Select your state</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-sm text-destructive">{errors.state}</p>
                )}
              </div>
            </div>

            {/* Postal Code and Country */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal Code *</Label>
                <Input
                  id="postalCode"
                  type="text"
                  maxLength={6}
                  value={formData.postalCode}
                  onChange={(e) => updateFormData('postalCode', e.target.value)}
                  className={errors.postalCode ? 'border-destructive' : ''}
                  placeholder="6-digit PIN code"
                />
                {errors.postalCode && (
                  <p className="text-sm text-destructive">{errors.postalCode}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value="India"
                  disabled
                  className="bg-secondary"
                />
              </div>
            </div>

            {/* Address Type */}
            <div className="space-y-3">
              <Label>Address Type</Label>
              <div className="flex flex-wrap gap-3">
                {addressTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => updateFormData('addressType', type.value)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                        formData.addressType === type.value
                          ? 'bg-primary text-white border-primary'
                          : 'bg-background text-foreground border-border hover:border-primary'
                      }`}
                    >
                      <IconComponent className="h-4 w-4" />
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Delivery Instructions */}
            <div className="space-y-2">
              <Label htmlFor="deliveryInstructions">Delivery Instructions</Label>
              <textarea
                id="deliveryInstructions"
                rows={3}
                value={formData.deliveryInstructions}
                onChange={(e) => updateFormData('deliveryInstructions', e.target.value)}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Any specific delivery instructions..."
              />
            </div>

            {/* Default Address Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={formData.isDefault}
                onChange={(e) => updateFormData('isDefault', e.target.checked)}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
              />
              <Label htmlFor="isDefault" className="text-sm font-normal">
                Set as default address
              </Label>
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <Button
                onClick={handleSave}
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {isEditing ? 'Update Address' : 'Save Address'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddEditAddressPage;